import React from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { pushToDataLayer } from '../../utils/gtm';
import { CombinedFullAppWebhookResult } from '../../utils/lendAPIPrequalHelper';
import { getPortalBranding, getVesselNoun } from '../../utils/portalBranding';
import './LendAPISuccessPage.scss';

interface LendAPISuccessPageProps {
  webhookResult: CombinedFullAppWebhookResult | null;
  source?: number | null;
}

const DOCUMENT_DISPLAY_NAMES: Record<string, string> = {
  "Applicant's Last Two Years Tax Returns including all schedules - Personal":
    'Last 2 years of personal tax returns (all schedules)',
  "Applicant's Last Two Years Tax Returns including K-1s - Business":
    'Last 2 years of business tax returns (including K-1s), if applicable',
};

function getDocumentDisplayName(docName: string): string {
  return DOCUMENT_DISPLAY_NAMES[docName] || docName;
}

function hasDocuments(result: CombinedFullAppWebhookResult | null): boolean {
  return !!(
    result?.autoRequestedDocs &&
    result.autoRequestedDocs.length > 0 &&
    result.docUploadUrl &&
    result.docUploadUrl.startsWith('https://')
  );
}

const WhatHappensNext: React.FC<{ showDocsBullet: boolean }> = ({ showDocsBullet }) => (
  <div className="lendapi-success__section">
    <p className="lendapi-success__subheading">What Happens Next:</p>
    <ul className="lendapi-success__bullet-list">
      <li>A loan officer reviews your application to match you with the best lending options.</li>
      {showDocsBullet ? (
        <li>Upload your documents to move your loan forward.</li>
      ) : (
        <li>
          We&apos;ll provide a link for you to upload the required documents to move your loan
          forward.
        </li>
      )}
      <li>
        Once uploaded, we&apos;ll contact you within 1 business day to discuss rates, terms and next
        steps.
      </li>
    </ul>
  </div>
);

const DocumentsSection: React.FC<{ docs: string[] }> = ({ docs }) => (
  <div className="lendapi-success__section">
    <p className="lendapi-success__subheading">Documents to Prepare:</p>
    <ul className="lendapi-success__bullet-list">
      {docs.map((doc) => (
        <li key={doc}>{getDocumentDisplayName(doc)}</li>
      ))}
    </ul>
  </div>
);

const UploadCard: React.FC<{ docUploadUrl: string }> = ({ docUploadUrl }) => (
  <div className="lendapi-success__upload-card">
    <p className="lendapi-success__upload-card-header">Upload Your Documents to Continue!</p>
    <div className="lendapi-success__checklist">
      <div className="lendapi-success__checklist-item">
        <span className="lendapi-success__checklist-icon">
          <CheckOutlined />
        </span>
        <span>Upload your document to speed up approval.</span>
      </div>
      <div className="lendapi-success__checklist-item">
        <span className="lendapi-success__checklist-icon">
          <CheckOutlined />
        </span>
        <span>Don&apos;t have everything yet? You can come back to finish.</span>
      </div>
      <div className="lendapi-success__checklist-item">
        <span className="lendapi-success__checklist-icon">
          <CheckOutlined />
        </span>
        <span>Questions? Call us at 888-386-3888 - we&apos;re here to help.</span>
      </div>
    </div>
    <a
      href={docUploadUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="lendapi-success__upload-button"
      onClick={() =>
        pushToDataLayer('link_click', {
          action_type: 'loan document upload',
          action_label: 'upload documents',
          click_location: 'main content',
        })
      }
    >
      Upload Documents
    </a>
  </div>
);

const LendAPISuccessPage: React.FC<LendAPISuccessPageProps> = ({ webhookResult, source }) => {
  const showDocuments = hasDocuments(webhookResult);
  const { label: portalLabel, rootUrl: portalRootUrl } = getPortalBranding(source);
  const vesselNoun = getVesselNoun(portalLabel);

  return (
    <div className="lendapi-success">
      <h2 className="lendapi-success__page-header">Application Submitted!</h2>

      <div className="lendapi-success__congrats">
        <h1 className="lendapi-success__congrats-heading">
          You&apos;re one step closer to your {vesselNoun}.
        </h1>
      </div>

      <WhatHappensNext showDocsBullet={showDocuments} />

      {showDocuments && webhookResult && (
        <>
          <DocumentsSection docs={webhookResult.autoRequestedDocs!} />
          <UploadCard docUploadUrl={webhookResult.docUploadUrl!} />
        </>
      )}

      {!showDocuments && (
        <p className="lendapi-success__closing-text">
          Our team is here to ensure the process is smooth and straightforward. Please wait for an
          email from our team.
        </p>
      )}

      <div className="lendapi-success__back-link-container">
        <a
          href={portalRootUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="lendapi-success__back-link"
        >
          Back to {portalLabel}
        </a>
      </div>
    </div>
  );
};

export default LendAPISuccessPage;
